import {
  collaborationRegionsData,
  type CollaborationRegion,
  type CollaborationRegionsData,
} from "../../../../../../shared/collaborationRegionsCatalog";
import { getDefaultIndirectVerificationCountryNames } from "../../../../../../shared/indirectVerificationDefaults";
import {
  buildProfileHeadquartersCountryGroups,
  getHeadquartersCountryPhoneIsoMap,
  type ProfileHeadquartersCountryGroup,
  type ProfileHeadquartersCountryItem,
} from "../../../../../../shared/profileHeadquartersCountries";
import {
  getPrimaryRegistrationLabel,
  getSecondaryRegistrationLabel,
} from "../../../../../../shared/registrationIdOptionsByCountry";

// FAQ Data
export interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
}

export interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  questions: FAQQuestion[];
}

export interface FAQData {
  categories: FAQCategory[];
}

export const faqData = {
  categories: [
    {
      id: "getting-started",
      title: "About the Platform",
      icon: "IconBook",
      questions: [
        {
          id: "what-is-gt-setu",
          question: "What is GTsetu?",
          answer:
            "GTsetu is a B2B collaboration platform that helps companies discover, evaluate, and connect with verified businesses across global markets for trade, manufacturing, distribution, and strategic partnerships.",
        },
        {
          id: "who-is-platform-for",
          question: "Who is the platform designed for?",
          answer:
            "The platform is built for manufacturers, distributors, traders, OEMs, service providers and companies exploring international partnerships, market entry, or supply chain collaborations.",
        },
        {
          id: "is-gt-setu-broker",
          question: "Is GTsetu a broker or intermediary?",
          answer:
            "No. GTsetu does not act as a broker, agent, or commission-based intermediary. The platform enables direct company-to-company discovery and connection.",
        },
        {
          id: "suitable-for-smes",
          question: "Is the platform suitable for SMEs?",
          answer:
            "Yes. The platform is designed to be accessible to SMEs while maintaining structured processes that also suit mid-sized and large enterprises.",
        },
      ],
    },
    {
      id: "discovery",
      title: "Discovery & Search",
      icon: "IconSearch",
      questions: [
        {
          id: "regions-industries",
          question: "Which regions and industries are supported?",
          answer:
            "The platform supports multiple industries across regions including Asia, the Middle East, Europe, Africa, Australia and the Americas. Industry coverage expands continuously based on verified demand and supply.",
        },
      ],
    },
    {
      id: "verification-trust",
      title: "Trust, Verification & Safety",
      icon: "IconShieldCheck",
      questions: [
        {
          id: "how-companies-verified",
          question: "How are companies verified on the platform?",
          answer:
            "Companies are verified through a combination of business information checks, domain or email validation, document review (where applicable), and manual screening before being allowed to engage fully.",
        },
        {
          id: "all-companies-verified",
          question: "Are all companies verified before connecting?",
          answer: "Yes. Basic verification is completed before companies can interact.",
        },
        {
          id: "prevent-spam",
          question: "How do you prevent spam or fake profiles?",
          answer:
            "The platform restricts anonymous outreach, limits connection attempts, and uses verification checks and monitoring to prevent misuse. Accounts that violate platform standards are suspended or removed.",
        },
        {
          id: "company-info-visibility",
          question: "Is my company information visible to everyone?",
          answer:
            "No. Public profiles are anonymised and limited. Company details such as company name, contact information, and documents are shared only after mutual interest or consent.",
        },
        {
          id: "authorized-contact",
          question: "How do I know the person contacting me is authorized?",
          answer:
            "Each user must declare their role and authority within the company. Role visibility, verification steps, and internal checks help ensure legitimate representation.",
        },
        {
          id: "misrepresentation",
          question: "What happens if a company misrepresents itself?",
          answer:
            "Accounts found misrepresenting information are restricted or removed. The platform may also revoke access and take corrective action to protect other users.",
        },
      ],
    },
    {
      id: "collaboration-workspace",
      title: "How Matching & Connections Work",
      icon: "IconUsers",
      questions: [
        {
          id: "how-matching-works",
          question: "How are companies matched on the platform?",
          answer:
            "Matching is based on structured inputs such as industry, intent, geography, engagement stage, and collaboration type. The goal is relevance, not volume.",
        },
        {
          id: "choose-connections",
          question: "Can I choose who to connect with?",
          answer:
            "Yes. Companies retain full control over whom they explore opportunities with and whether they proceed with a connection.",
        },
        {
          id: "automated-or-curated",
          question: "Are connections automated or curated?",
          answer:
            "Initial discovery is system-assisted, while connections are based on mutual interest. Certain introductions may also be curated to maintain quality.",
        },
        {
          id: "after-mutual-interest",
          question: "What happens after two companies show interest?",
          answer:
            "Once interest is mutual, companies can proceed to deeper discussions, exchange information, and optionally sign an NDA before sharing sensitive details.",
        },
        {
          id: "connection-limits",
          question: "How many companies can I connect with?",
          answer:
            "Connection limits depend on the access level. During early access or free periods, limits may apply to maintain quality and prevent misuse.",
        },
      ],
    },
    {
      id: "payment-pricing",
      title: "Pricing, Free Access & Payments",
      icon: "IconFileInvoice",
      questions: [
        {
          id: "is-platform-free",
          question: "Is the platform free to use?",
          answer:
            "The platform may offer a limited free or early-access period. This allows companies to explore the platform before committing to a paid plan.",
        },
        {
          id: "free-access-included",
          question: "What is included in the free access period?",
          answer:
            "Free access typically includes profile creation, discovery, limited expressions of interest, and basic visibility. Advanced features may require paid activation.",
        },
        {
          id: "when-paid-plans-start",
          question: "When do paid plans start?",
          answer:
            "Paid plans begin after the free or early-access period ends, or when a company chooses to unlock advanced features earlier.",
        },
        {
          id: "success-fee",
          question: "Do you charge a success fee or commission?",
          answer: "No. GTsetu does not charge commissions or success fees on deals concluded between companies.",
        },
      ],
    },
    {
      id: "security-privacy",
      title: "Legal, NDA & Data Privacy",
      icon: "IconLock",
      questions: [
        {
          id: "data-protection",
          question: "How is my data protected?",
          answer:
            "Company data is handled in accordance with applicable data protection standards. Access is controlled, purpose-driven, and never shared without consent.",
        },
        {
          id: "nda-signing",
          question: "Do companies sign an NDA before sharing sensitive information?",
          answer:
            "NDAs are optional but strongly recommended before sharing confidential or proprietary information. The platform facilitates NDA workflows where applicable.",
        },
        {
          id: "commercial-agreements",
          question: "Is GTsetu a party to commercial agreements?",
          answer:
            "No. All commercial agreements are entered into directly between the companies involved. The platform does not participate in or enforce contracts.",
        },
      ],
    },
  ],
};

// Countries Data with Registration Requirements (full world HQ; see shared/profileHeadquartersCountries)
export type CountryItem = ProfileHeadquartersCountryItem;
export type CountryGroup = ProfileHeadquartersCountryGroup;

export interface CountriesData {
  countryGroups: CountryGroup[];
}

export const countriesData: CountriesData = {
  countryGroups: buildProfileHeadquartersCountryGroups(),
};

/** Grouped options for Mantine Select: `value` is persisted HQ country name; `label` may be longer for display. */
export function buildGroupedCountrySelectOptions(): {
  group: string;
  items: { value: string; label: string }[];
}[] {
  return countriesData.countryGroups.map((group) => ({
    group: group.group,
    items: group.items.map((item) => ({
      value: item.name,
      label: item.selectLabel ?? item.name,
    })),
  }));
}

export const COUNTRIES_FILTER_DATA = buildGroupedCountrySelectOptions();

// Helper function to get registration name by country
export const getRegistrationNameByCountry = (countryName: string): string | null => {
  for (const group of countriesData.countryGroups) {
    const country = group.items.find((c) => c.name === countryName);
    if (country) {
      return country.registrationName;
    }
  }
  return null;
};


export function getIndirectVerificationHeadquartersCountriesWeb(): string[] {
  const raw =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_INDIRECT_VERIFICATION_COUNTRIES
      ? String(import.meta.env.VITE_INDIRECT_VERIFICATION_COUNTRIES)
      : "";
  if (!raw.trim()) {
    return [...getDefaultIndirectVerificationCountryNames()];
  }
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

export function headquartersUsesIndirectCountryRegistrationWeb(
  headquartersCountry: string | null | undefined
): boolean {
  const hq = headquartersCountry?.trim() ?? "";
  if (!hq) {
    return false;
  }
  return getIndirectVerificationHeadquartersCountriesWeb().some((c) => c === hq);
}

export const formatRegistrationIdTypeLabel = (
  type: string | null | undefined,
  headquartersCountry?: string | null
): string => {
  if (!type) return "N/A";
  if (type === "NZBN") return "NZBN";
  if (type === "COUNTRY_REGISTRATION") {
    return (
      getPrimaryRegistrationLabel(headquartersCountry) ||
      getRegistrationNameByCountry(headquartersCountry ?? "") ||
      "Country registration ID"
    );
  }
  if (type === "LOCAL_REGISTRY") {
    if (headquartersCountry === "New Zealand") return "NZBN";
    return (
      getSecondaryRegistrationLabel(headquartersCountry) ||
      getPrimaryRegistrationLabel(headquartersCountry) ||
      getRegistrationNameByCountry(headquartersCountry ?? "") ||
      "Official registration ID"
    );
  }
  if (type === "LEI") return "LEI";
  if (type === "EU_VAT") {
    if (headquartersCountry?.trim()) return getEuVatLabelForHeadquartersCountry(headquartersCountry);
    return "EU VAT (VIES)";
  }
  if (type === "USCC") return "Unified Social Credit Code (USCC)";
  if (type === "GST") return "GST (Goods and Services Tax)";
  if (type === "CIN") return "CIN (Corporate Identification Number)";
  if (type === "UDM") return "Udyam (MSME registration)";
  if (type === "KYB") return "Registry (KYB)";
  if (type === "UBN") return "Unified Business Number (UBN)";
  if (type === "CRN") return "Company Registration Number (CRN)";
  if (type === "NOGN") return getRegistrationNameByCountry("Norway") || "Organisation Number";
  if (type === "SIREN") return getRegistrationNameByCountry("France") || "SIREN / SIRET";
  return type;
};

/** ISO 3166-1 alpha-2 for `react-international-phone` defaultCountry from headquarters country name */
export const HEADQUARTERS_COUNTRY_PHONE_ISO: Record<string, string> =
  getHeadquartersCountryPhoneIsoMap();

// Helper function to get all country options for Select dropdown (value = canonical name)
export const getCountryNamesForSelect = () => buildGroupedCountrySelectOptions();

export interface CountryCodeItem {
  name: string;
  code: string;
}

// TheKYB coverage list from https://developers.thekyb.com/docs/kyb_coverage (deduped for repeated entries)
export const THE_KYB_COUNTRY_CODES: CountryCodeItem[] = [
  { name: "Abu Dhabi", code: "AE" },
  { name: "Afghanistan", code: "AF" },
  { name: "Albania", code: "AL" },
  { name: "Algeria", code: "DZ" },
  { name: "Andorra", code: "AD" },
  { name: "Angola", code: "AO" },
  { name: "Anguilla", code: "AI" },
  { name: "Antigua and Barbuda", code: "AG" },
  { name: "American Samoa", code: "AS" },
  { name: "Antarctica", code: "AQ" },
  { name: "Argentina", code: "AR" },
  { name: "Armenia", code: "AM" },
  { name: "Aruba", code: "AW" },
  { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" },
  { name: "Azerbaijan", code: "AZ" },
  { name: "Bahamas", code: "BS" },
  { name: "Bahrain", code: "BH" },
  { name: "Bangladesh", code: "BD" },
  { name: "Barbados", code: "BB" },
  { name: "Belarus", code: "BY" },
  { name: "Belgium", code: "BE" },
  { name: "Belize", code: "BZ" },
  { name: "Benin", code: "BJ" },
  { name: "Bermuda", code: "BM" },
  { name: "Bhutan", code: "BT" },
  { name: "Bolivia", code: "BO" },
  { name: "Bosnia and Herzegovina", code: "BA" },
  { name: "Botswana", code: "BW" },
  { name: "Brazil", code: "BR" },
  { name: "British Indian Ocean Territory", code: "IO" },
  { name: "Brunei", code: "BN" },
  { name: "Bulgaria", code: "BG" },
  { name: "Burkina Faso", code: "BF" },
  { name: "Burma (Myanmar)", code: "MM" },
  { name: "Burundi", code: "BI" },
  { name: "Cambodia", code: "KH" },
  { name: "Cameroon", code: "CM" },
  { name: "Canada", code: "CA" },
  { name: "Cape Verde", code: "CV" },
  { name: "Cayman Islands", code: "KY" },
  { name: "Central African Republic", code: "CF" },
  { name: "Chad", code: "TD" },
  { name: "Chile", code: "CL" },
  { name: "China", code: "CN" },
  { name: "Christmas Island", code: "CX" },
  { name: "Cocos (Keeling) Islands", code: "CC" },
  { name: "Colombia", code: "CO" },
  { name: "Comoros", code: "KM" },
  { name: "Congo, Democratic Republic", code: "CD" },
  { name: "Congo, Republic", code: "CG" },
  { name: "Cook Islands", code: "CK" },
  { name: "Costa Rica", code: "CR" },
  { name: "Croatia", code: "HR" },
  { name: "Cuba", code: "CU" },
  { name: "Curacao", code: "CW" },
  { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Djibouti", code: "DJ" },
  { name: "Dominica", code: "DM" },
  { name: "Dominican Republic", code: "DO" },
  { name: "East Timor", code: "TL" },
  { name: "Ecuador", code: "EC" },
  { name: "Egypt", code: "EG" },
  { name: "El Salvador", code: "SV" },
  { name: "Equatorial Guinea", code: "GQ" },
  { name: "Eritrea", code: "ER" },
  { name: "Estonia", code: "EE" },
  { name: "Eswatini", code: "SZ" },
  { name: "Ethiopia", code: "ET" },
  { name: "Falkland Islands", code: "FK" },
  { name: "Faroe Islands", code: "FO" },
  { name: "Fiji", code: "FJ" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "French Guiana", code: "GF" },
  { name: "French Polynesia", code: "PF" },
  { name: "French Southern Territories", code: "TF" },
  { name: "Gabon", code: "GA" },
  { name: "Gambia", code: "GM" },
  { name: "Georgia", code: "GE" },
  { name: "Germany", code: "DE" },
  { name: "Ghana", code: "GH" },
  { name: "Gibraltar", code: "GI" },
  { name: "Greece", code: "GR" },
  { name: "Greenland", code: "GL" },
  { name: "Grenada", code: "GD" },
  { name: "Guadeloupe", code: "GP" },
  { name: "Guam", code: "GU" },
  { name: "Guatemala", code: "GT" },
  { name: "Guernsey", code: "GG" },
  { name: "Guinea", code: "GN" },
  { name: "Guinea-Bissau", code: "GW" },
  { name: "Guyana", code: "GY" },
  { name: "Haiti", code: "HT" },
  { name: "Heard Island and McDonald Islands", code: "HM" },
  { name: "Honduras", code: "HN" },
  { name: "Hong Kong", code: "HK" },
  { name: "Hungary", code: "HU" },
  { name: "Iceland", code: "IS" },
  { name: "India", code: "IN" },
  { name: "Indonesia", code: "ID" },
  { name: "Iran", code: "IR" },
  { name: "Iraq", code: "IQ" },
  { name: "Ireland", code: "IE" },
  { name: "Israel", code: "IL" },
  { name: "Italy", code: "IT" },
  { name: "Ivory Coast", code: "CI" },
  { name: "Jamaica", code: "JM" },
  { name: "Japan", code: "JP" },
  { name: "Jersey", code: "JE" },
  { name: "Jordan", code: "JO" },
  { name: "Kazakhstan", code: "KZ" },
  { name: "Kenya", code: "KE" },
  { name: "Kiribati", code: "KI" },
  { name: "Korea, Dem. Republic of", code: "KP" },
  { name: "Kuwait", code: "KW" },
  { name: "Kyrgyzstan", code: "KG" },
  { name: "Laos", code: "LA" },
  { name: "Latvia", code: "LV" },
  { name: "Lebanon", code: "LB" },
  { name: "Lesotho", code: "LS" },
  { name: "Liberia", code: "LR" },
  { name: "Libya", code: "LY" },
  { name: "Liechtenstein", code: "LI" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Macao (Macau)", code: "MO" },
  { name: "Macedonia", code: "MK" },
  { name: "Madagascar", code: "MG" },
  { name: "Malawi", code: "MW" },
  { name: "Malaysia", code: "MY" },
  { name: "Maldives", code: "MV" },
  { name: "Mali", code: "ML" },
  { name: "Malta", code: "MT" },
  { name: "Man Island", code: "IM" },
  { name: "Marshall Islands", code: "MH" },
  { name: "Martinique", code: "MQ" },
  { name: "Mauritania", code: "MR" },
  { name: "Mauritius", code: "MU" },
  { name: "Mayotte", code: "YT" },
  { name: "Mexico", code: "MX" },
  { name: "Micronesia", code: "FM" },
  { name: "Moldova", code: "MD" },
  { name: "Monaco", code: "MC" },
  { name: "Mongolia", code: "MN" },
  { name: "Montenegro", code: "ME" },
  { name: "Montserrat", code: "MS" },
  { name: "Morocco", code: "MA" },
  { name: "Mozambique", code: "MZ" },
  { name: "Namibia", code: "NA" },
  { name: "Nauru", code: "NR" },
  { name: "Nepal", code: "NP" },
  { name: "Netherlands", code: "NL" },
  { name: "Netherlands Antilles", code: "AN" },
  { name: "New Caledonia", code: "NC" },
  { name: "New Zealand", code: "NZ" },
  { name: "Nicaragua", code: "NI" },
  { name: "Niger", code: "NE" },
  { name: "Nigeria", code: "NG" },
  { name: "Niue", code: "NU" },
  { name: "Norfolk Island", code: "NF" },
  { name: "Northern Mariana Islands", code: "MP" },
  { name: "Norway", code: "NO" },
  { name: "Oman", code: "OM" },
  { name: "Pakistan", code: "PK" },
  { name: "Palau", code: "PW" },
  { name: "Palestinian Territories", code: "PS" },
  { name: "Panama", code: "PA" },
  { name: "Papua New Guinea", code: "PG" },
  { name: "Paraguay", code: "PY" },
  { name: "Peru", code: "PE" },
  { name: "Philippines", code: "PH" },
  { name: "Pitcairn", code: "PN" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Puerto Rico", code: "PR" },
  { name: "Qatar", code: "QA" },
  { name: "Republic of Kosovo", code: "XK" },
  { name: "Reunion Island", code: "RE" },
  { name: "Romania", code: "RO" },
  { name: "Russia Federation", code: "RU" },
  { name: "Rwanda", code: "RW" },
  { name: "Saint Barthelemy", code: "BL" },
  { name: "Saint Kitts and Nevis", code: "KN" },
  { name: "Saint Lucia", code: "LC" },
  { name: "Saint Martin", code: "MF" },
  { name: "Saint Pierre and Miquelon", code: "PM" },
  { name: "Saint Vincent and the Grenadines", code: "VC" },
  { name: "Samoa", code: "WS" },
  { name: "San Marino", code: "SM" },
  { name: "Saudi Arabia", code: "SA" },
  { name: "Senegal", code: "SN" },
  { name: "Serbia", code: "RS" },
  { name: "Seychelles", code: "SC" },
  { name: "Sierra Leone", code: "SL" },
  { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Solomon Islands", code: "SB" },
  { name: "Somalia", code: "SO" },
  { name: "South Africa", code: "ZA" },
  { name: "South Georgia and the South Sandwich Islands", code: "GS" },
  { name: "South Korea", code: "KR" },
  { name: "South Sudan", code: "SS" },
  { name: "Spain", code: "ES" },
  { name: "Sri Lanka", code: "LK" },
  { name: "Sudan", code: "SD" },
  { name: "Suriname", code: "SR" },
  { name: "Svalbard and Jan Mayen", code: "SJ" },
  { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" },
  { name: "Syria", code: "SY" },
  { name: "Sao Tome and Principe", code: "ST" },
  { name: "Taiwan", code: "TW" },
  { name: "Tajikistan", code: "TJ" },
  { name: "Tanzania", code: "TZ" },
  { name: "Thailand", code: "TH" },
  { name: "Togo", code: "TG" },
  { name: "Tokelau", code: "TK" },
  { name: "Tonga", code: "TO" },
  { name: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", code: "TN" },
  { name: "Turkey", code: "TR" },
  { name: "Turkmenistan", code: "TM" },
  { name: "Turks and Caicos Islands", code: "TC" },
  { name: "Tuvalu", code: "TV" },
  { name: "Uganda", code: "UG" },
  { name: "Ukraine", code: "UA" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", code: "GB" },
  { name: "Uruguay", code: "UY" },
  { name: "Uzbekistan", code: "UZ" },
  { name: "Vanuatu", code: "VU" },
  { name: "Vatican City State", code: "VA" },
  { name: "Venezuela", code: "VE" },
  { name: "Vietnam", code: "VN" },
  { name: "Virgin Islands, British", code: "VG" },
  { name: "Virgin Islands, US", code: "VI" },
  { name: "Wallis and Futuna", code: "WF" },
  { name: "Western Sahara", code: "EH" },
  { name: "Yemen", code: "YE" },
  { name: "Zambia", code: "ZM" },
  { name: "Zimbabwe", code: "ZW" },
];

/** EU members that participate in VIES (GB excluded — Northern Ireland uses XI VAT). */
export const EU_MEMBER_STATE_ISO_FOR_VIES_UI = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
]);

/**
 * Whether the HQ country (as in `THE_KYB_COUNTRY_CODES`) may use EU VAT (VIES) in the profile form.
 * United Kingdom uses Company Registration Number (CRN) + LEI instead of VAT on the frontend.
 * Norway uses organisasjonsnummer (NOGN) + LEI instead of VAT on the frontend.
 * France uses SIREN/SIRET (INSEE) + LEI alongside EU VAT on the frontend.
 */
export function headquartersCountrySupportsEuVatOption(countryName: string | null | undefined): boolean {
  if (!countryName?.trim()) return false;
  const row = THE_KYB_COUNTRY_CODES.find((c) => c.name === countryName.trim());
  if (!row) return false;
  if (row.code === "GB") return false;
  return EU_MEMBER_STATE_ISO_FOR_VIES_UI.has(row.code);
}

/** Local VAT / VIES field labels by HQ ISO 3166-1 alpha-2 (GR = Greece; GB uses XI-style VAT for UK NI path). */
export const EU_VAT_FIELD_LABEL_BY_ISO2: Record<string, string> = {
  AT: "UID (Umsatzsteuer-Identifikationsnummer)",
  BE: "Numéro de TVA / BTW-nummer",
  BG: "VAT Number (ДДС номер)",
  HR: "OIB (Osobni identifikacijski broj)",
  CY: "VAT Registration Number",
  CZ: "DIČ (Daňové identifikační číslo)",
  DK: "CVR Number",
  EE: "KMKR Number",
  FI: "ALV-numero / VAT Number",
  FR: "Numéro de TVA",
  DE: "USt-IdNr.",
  GR: "ΑΦΜ (AFM – Tax Identification Number)",
  HU: "Közösségi adószám",
  IE: "VAT Number",
  IT: "Partita IVA",
  LV: "PVN Number",
  LT: "PVM mokėtojo kodas",
  LU: "Numéro de TVA",
  MT: "VAT Registration Number",
  NL: "BTW-nummer",
  PL: "NIP (Numer Identyfikacji Podatkowej)",
  PT: "NIF (Número de Identificação Fiscal)",
  RO: "CUI (Cod Unic de Identificare)",
  SK: "IČ DPH",
  SI: "ID za DDV",
  ES: "NIF (Número de Identificación Fiscal)",
  SE: "Momsregistreringsnummer",
  GB: "VAT Number (XI VAT)",
};

export function getEuVatLabelForHeadquartersCountry(countryName: string | null | undefined): string {
  if (!countryName?.trim()) return "VAT number";
  const row = THE_KYB_COUNTRY_CODES.find((c) => c.name === countryName.trim());
  if (!row) return "VAT number";
  if (row.code === "GB") return EU_VAT_FIELD_LABEL_BY_ISO2.GB;
  if (!EU_MEMBER_STATE_ISO_FOR_VIES_UI.has(row.code)) return "VAT number";
  return EU_VAT_FIELD_LABEL_BY_ISO2[row.code] ?? "VAT number";
}

/** VIES VAT number country prefix (EL for Greece, XI for United Kingdom per NI VAT). */
export function getViesVatPrefixForHeadquartersCountry(countryName: string | null | undefined): string | null {
  if (!countryName?.trim()) return null;
  const row = THE_KYB_COUNTRY_CODES.find((c) => c.name === countryName.trim());
  if (!row) return null;
  if (row.code === "GB") return "XI";
  if (row.code === "GR") return "EL";
  if (!EU_MEMBER_STATE_ISO_FOR_VIES_UI.has(row.code)) return null;
  return row.code;
}

export function getEuVatPlaceholderForHeadquartersCountry(countryName: string | null | undefined): string {
  const prefix = getViesVatPrefixForHeadquartersCountry(countryName);
  if (!prefix) return "e.g. DE123456789";
  return `e.g. ${prefix}123456789`;
}

export function looksLikeEuVatRegistrationNumber(raw: string): boolean {
  const s = raw.trim().toUpperCase().replace(/\s/g, "");
  if (s.length < 8 || s.length > 15 || s.length === 20) return false;
  return /^[A-Z]{2}[0-9A-Z+*.]{2,}$/.test(s);
}

export const THE_KYB_US_STATE_CODES: CountryCodeItem[] = [
  { name: "Alabama", code: "US_AL" },
  { name: "Alaska", code: "US_AK" },
  { name: "Arizona", code: "US_AZ" },
  { name: "Arkansas", code: "US_AR" },
  { name: "California", code: "US_CA" },
  { name: "Colorado", code: "US_CO" },
  { name: "Connecticut", code: "US_CT" },
  { name: "Delaware", code: "US_DE" },
  { name: "District of Columbia", code: "US_DC" },
  { name: "Florida", code: "US_FL" },
  { name: "Georgia", code: "US_GA" },
  { name: "Hawaii", code: "US_HI" },
  { name: "Idaho", code: "US_ID" },
  { name: "Indiana", code: "US_IN" },
  { name: "Iowa", code: "US_IA" },
  { name: "Kansas", code: "US_KS" },
  { name: "Kentucky", code: "US_KY" },
  { name: "Louisiana", code: "US_LA" },
  { name: "Maryland", code: "US_MD" },
  { name: "Massachusetts", code: "US_MA" },
  { name: "Michigan", code: "US_MI" },
  { name: "Minnesota", code: "US_MN" },
  { name: "Mississippi", code: "US_MS" },
  { name: "Montana", code: "US_MT" },
  { name: "Nebraska", code: "US_NE" },
  { name: "New Hampshire", code: "US_NH" },
  { name: "New Jersey", code: "US_NJ" },
  { name: "New Mexico", code: "US_NM" },
  { name: "New York", code: "US_NY" },
  { name: "North Carolina", code: "US_NC" },
  { name: "North Dakota", code: "US_ND" },
  { name: "Ohio", code: "US_OH" },
  { name: "Oklahoma", code: "US_OK" },
  { name: "Oregon", code: "US_OR" },
  { name: "Rhode Island", code: "US_RI" },
  { name: "South Carolina", code: "US_SC" },
  { name: "South Dakota", code: "US_SD" },
  { name: "Tennessee", code: "US_TN" },
  { name: "Texas", code: "US_TX" },
  { name: "Vermont", code: "US_VT" },
  { name: "Virginia", code: "US_VA" },
  { name: "Washington", code: "US_WA" },
  { name: "West Virginia", code: "US_WV" },
  { name: "Wisconsin", code: "US_WI" },
  { name: "Wyoming", code: "US_WY" },
  { name: "Pennsylvania", code: "US_PA" },
  { name: "Illinois", code: "US_IL" },
  { name: "Maine", code: "US_ME" },
  { name: "Nevada", code: "US_NV" },
  { name: "Utah", code: "US_UT" },
  { name: "Missouri", code: "US_MO" },
];

export const THE_KYB_COUNTRY_CODE_BY_COUNTRY_NAME: Record<string, string> = {
  Japan: "JP",
  Thailand: "TH",
  Taiwan: "TW",
  Singapore: "SG",
  India: "IN",
  "South Korea": "KR",
  "Sri Lanka": "LK",
  Malaysia: "MY",
  Indonesia: "ID",
  Vietnam: "VN",
  Philippines: "PH",
  "China (Mainland)": "CN",
  "China (Hong Kong)": "HK",
  Bangladesh: "BD",
  Nepal: "NP",
  Bhutan: "BT",
  "United Arab Emirates (UAE)": "AE",
  Oman: "OM",
  Kuwait: "KW",
  "Saudi Arabia": "SA",
  Turkey: "TR",
  Egypt: "EG",
  Qatar: "QA",
  Bahrain: "BH",
  Israel: "IL",
  Austria: "AT",
  Belgium: "BE",
  Bulgaria: "BG",
  Croatia: "HR",
  Cyprus: "CY",
  "Czech Republic": "CZ",
  Denmark: "DK",
  Estonia: "EE",
  Finland: "FI",
  France: "FR",
  Germany: "DE",
  Greece: "GR",
  Hungary: "HU",
  Ireland: "IE",
  Italy: "IT",
  Latvia: "LV",
  Lithuania: "LT",
  Luxembourg: "LU",
  Malta: "MT",
  Netherlands: "NL",
  Norway: "NO",
  Poland: "PL",
  Portugal: "PT",
  Romania: "RO",
  "Russia Federation": "RU",
  Slovakia: "SK",
  Slovenia: "SI",
  Spain: "ES",
  Sweden: "SE",
  "United Kingdom": "GB",
  "South Africa": "ZA",
  Kenya: "KE",
  Morocco: "MA",
  Australia: "AU",
  "New Zealand": "NZ",
  USA: "US",
  Canada: "CA",
  Mexico: "MX",
  Brazil: "BR",
};

export const getTheKybCountryCodeByCountryName = (countryName?: string | null): string | null =>
  countryName ? THE_KYB_COUNTRY_CODE_BY_COUNTRY_NAME[countryName] ?? null : null;

// Operating Industry Enum Labels
export const OPERATING_INDUSTRY_LABELS: Record<string, string> = {
  OIL_AND_GAS: "Oil & Gas",
  DEFENCE_AND_AEROSPACE: "Defence & Aerospace",
  SHIPPING_MARITIME_PORTS: "Shipping, Maritime & Ports",
  POWER_AND_ENERGY: "Power & Energy",
  MANUFACTURING_AND_INDUSTRIAL_AUTOMATION: "Manufacturing & Industrial Automation",
  ELECTRONICS_AND_SEMICONDUCTORS: "Electronics & Semiconductors",
  AGRICULTURE_AND_FOOD_PROCESSING: "Agriculture & Food Processing",
  AUTOMOTIVE_AND_EV: "Automotive & EV",
  CHEMICALS_AND_SPECIALTY_MATERIALS: "Chemicals & Specialty Materials",
  MINING: "Mining",
  METALS_AND_MINERALS: "Metals & Minerals",
  CONSTRUCTION_AND_EPC: "Construction & EPC",
  WATER_AND_ENVIRONMENTAL_SOLUTIONS: "Water & Environmental Solutions",
  HEALTHCARE_AND_LIFE_SCIENCES: "Healthcare & Life Sciences",
  ICT_AND_DIGITAL_TECH: "Software & Digital Solutions Company",
  CLIMATE_TECH_AND_SUSTAINABILITY: "Climate Tech & Sustainability",
  INDUSTRIAL_MACHINERY_TOOLS_AND_CAPITAL_EQUIPMENT: "Industrial Machinery, Tools & Capital Equipment",
  PLASTICS_POLYMERS_AND_RUBBER_MANUFACTURING: "Plastics, Polymers & Rubber Manufacturing",
  CONSUMER_PRODUCTS_AND_FMCG: "Consumer Products & FMCG",
  TEXTILES_APPAREL_AND_TECHNICAL_FABRICS: "Textiles, Apparel & Technical Fabrics",
  PACKAGING_PRINTING_AND_CONVERTING_SOLUTIONS: "Packaging, Printing & Converting Solutions",
};

// Sub-Industry Labels
export const SUB_INDUSTRY_LABELS: Record<string, string> = {
  // Oil & Gas
  OIL_GAS_UPSTREAM_EXPLORATION_PRODUCTION: "Upstream – Exploration & Production",
  OIL_GAS_MIDSTREAM_TRANSPORTATION_STORAGE: "Midstream – Transportation & Storage",
  OIL_GAS_DOWNSTREAM_REFINING_PETROCHEMICALS: "Downstream – Refining & Petrochemicals",
  OIL_GAS_OILFIELD_EQUIPMENT_SERVICES: "Oilfield Equipment & Services",
  OIL_GAS_LNG_GAS_PROCESSING: "LNG & Gas Processing",
  OIL_GAS_INDUSTRIAL_SAFETY_COMPLIANCE: "Industrial Safety & Compliance",

  // Defence & Aerospace
  DEFENCE_AERO_COMPONENTS_ASSEMBLIES: "Aerospace Components & Assemblies",
  DEFENCE_AERO_ELECTRONICS_SYSTEMS: "Defence Electronics & Systems",
  DEFENCE_AERO_AVIONICS_NAVIGATION: "Avionics & Navigation",
  DEFENCE_AERO_MRO: "MRO (Maintenance, Repair & Overhaul)",
  DEFENCE_AERO_SPACE_SYSTEMS_SATELLITES: "Space Systems & Satellites",
  DEFENCE_AERO_GROUND_SUPPORT_EQUIPMENT: "Ground Support Equipment",

  // Shipping, Maritime & Ports
  MARITIME_SHIPBUILDING_REPAIR: "Shipbuilding & Ship Repair",
  MARITIME_MARINE_EQUIPMENT_SYSTEMS: "Marine Equipment & Systems",
  MARITIME_PORT_INFRASTRUCTURE_HANDLING: "Port Infrastructure & Cargo Handling",
  MARITIME_LOGISTICS_TECHNOLOGY: "Maritime Logistics & Technology",
  MARITIME_OFFSHORE_STRUCTURES_SERVICES: "Offshore Structures & Services",

  // Manufacturing & Industrial Automation
  MANUFACTURING_FACTORY_AUTOMATION_ROBOTICS: "Factory Automation & Robotics",
  MANUFACTURING_INDUSTRIAL_CONTROLS_PLCS: "Industrial Controls & PLCs",
  MANUFACTURING_SMART_MANUFACTURING_INDUSTRY_4: "Smart Manufacturing & Industry 4.0",
  MANUFACTURING_SENSORS_INSTRUMENTATION: "Sensors & Instrumentation",
  MANUFACTURING_INDUSTRIAL_SOFTWARE: "Industrial Software",

  // Electronics & Semiconductors
  ELECTRONICS_SEMICONDUCTOR_DEVICES_ICS: "Semiconductor Devices & ICs",
  ELECTRONICS_PCB_DESIGN_ASSEMBLY: "PCB Design & Assembly",
  ELECTRONICS_COMPONENTS_CONNECTORS: "Electronic Components & Connectors",
  ELECTRONICS_CONSUMER_INDUSTRIAL: "Consumer & Industrial Electronics",
  ELECTRONICS_EMS: "EMS (Electronics Manufacturing Services)",

  // Agriculture & Food Processing
  AGRI_MACHINERY_EQUIPMENT: "Agricultural Machinery & Equipment",
  AGRI_FOOD_PROCESSING_PACKAGING: "Food Processing & Packaging",
  AGRI_COLD_CHAIN_STORAGE: "Cold Chain & Storage Solutions",
  AGRI_INPUTS_SEEDS_FERTILISERS: "Agri-Inputs (Seeds, Fertilisers, Crop Protection)",
  AGRI_PRECISION_FARMING_TECH: "Precision Farming & AgriTech",

  // Automotive & EV
  AUTOMOTIVE_ICE_COMPONENTS: "ICE (Internal Combustion Engine) Components",
  AUTOMOTIVE_EV_POWERTRAIN_DRIVETRAIN: "EV Powertrain & Drivetrain",
  AUTOMOTIVE_BATTERIES_CHARGING: "Batteries & Charging Infrastructure",
  AUTOMOTIVE_ELECTRONICS: "Automotive Electronics",
  AUTOMOTIVE_TIER1_TIER2_COMPONENTS: "Tier-1 / Tier-2 Components",

  // Chemicals & Specialty Materials
  CHEMICALS_BASIC_COMMODITY: "Basic & Commodity Chemicals",
  CHEMICALS_SPECIALTY_PERFORMANCE: "Specialty & Performance Chemicals",
  CHEMICALS_INDUSTRIAL_GASES: "Industrial Gases",
  CHEMICALS_COATINGS_ADHESIVES_SEALANTS: "Coatings, Adhesives & Sealants",
  CHEMICALS_ADDITIVES_CATALYSTS: "Additives & Catalysts",

  // Metals, Minerals & Mining
  METALS_FERROUS_NON_FERROUS: "Ferrous & Non-Ferrous Metals",
  METALS_MINING_EQUIPMENT_SERVICES: "Mining Equipment & Services",
  METALS_MINERAL_PROCESSING: "Mineral Processing",
  METALS_FOUNDRIES_CASTINGS: "Foundries & Castings",
  METALS_METAL_FABRICATION: "Metal Fabrication",

  // Construction & EPC
  CONSTRUCTION_INFRASTRUCTURE_DEVELOPMENT: "Infrastructure Development",
  CONSTRUCTION_INDUSTRIAL: "Industrial Construction",
  CONSTRUCTION_EPC_CONTRACTORS: "EPC Contractors",
  CONSTRUCTION_PRE_ENGINEERED_BUILDINGS: "Pre-Engineered Buildings",
  CONSTRUCTION_MATERIALS_SYSTEMS: "Construction Materials & Systems",

  // Water & Environmental
  WATER_TREATMENT_SYSTEMS: "Water Treatment Systems",
  WATER_WASTEWATER_MANAGEMENT: "Wastewater Management",
  WATER_DESALINATION_TECHNOLOGIES: "Desalination Technologies",
  WATER_INDUSTRIAL_EFFLUENT_TREATMENT: "Industrial Effluent Treatment",
  WATER_ENVIRONMENTAL_MONITORING: "Environmental Monitoring & Compliance",

  // Healthcare & Life Sciences
  HEALTHCARE_MEDICAL_DEVICES: "Medical Devices & Equipment",
  HEALTHCARE_DIAGNOSTICS_IMAGING: "Diagnostics & Imaging",
  HEALTHCARE_LIFE_SCIENCES_TOOLS: "Life Sciences Tools & Reagents",
  HEALTHCARE_PHARMA_MANUFACTURING: "Pharmaceutical Manufacturing",
  HEALTHCARE_HOSPITAL_INFRASTRUCTURE: "Hospital Infrastructure",

  // Power & Energy
  ENERGY_THERMAL_POWER_SYSTEMS: "Thermal Power Systems",
  ENERGY_RENEWABLE_EQUIPMENT: "Renewable Energy Equipment",
  ENERGY_STORAGE_SYSTEMS: "Energy Storage Systems",
  ENERGY_TRANSMISSION_DISTRIBUTION: "Transmission & Distribution",
  ENERGY_POWER_PLANT_EPC_OM: "Power Plant EPC & O&M",

  // Industrial Machinery & Capital Equipment
  MACHINERY_MACHINE_TOOLS_CNC: "Machine Tools & CNC",
  MACHINERY_HEAVY_INDUSTRIAL: "Heavy Industrial Machinery",
  MACHINERY_PRODUCTION_LINES_TURNKEY: "Production Lines & Turnkey Systems",
  MACHINERY_MATERIAL_HANDLING: "Material Handling Equipment",
  MACHINERY_INDUSTRIAL_TOOLING: "Industrial Tooling & Fixtures",

  // Plastics, Polymers & Rubber
  PLASTICS_INJECTION_MOULDING: "Injection Moulding",
  PLASTICS_EXTRUSION_BLOW_MOULDING: "Extrusion & Blow Moulding",
  PLASTICS_RUBBER_ELASTOMERS: "Rubber & Elastomers",
  PLASTICS_INDUSTRIAL_RESINS: "Industrial Resins & Compounds",
  PLASTICS_POLYMER_COMPOUNDING: "Polymer Compounding",

  // Consumer Products & FMCG
  CONSUMER_HOME_PERSONAL_CARE: "Home & Personal Care",
  CONSUMER_APPLIANCES: "Consumer Appliances",
  CONSUMER_LIFESTYLE_PRODUCTS: "Lifestyle Products",
  CONSUMER_PRIVATE_LABEL: "Private Label Manufacturing",
  CONSUMER_OEM_ODM: "OEM / ODM Services",

  // Textiles & Fabrics
  TEXTILES_APPAREL_GARMENTS: "Apparel & Garments",
  TEXTILES_TECHNICAL_INDUSTRIAL: "Technical & Industrial Textiles",
  TEXTILES_NON_WOVEN_COMPOSITES: "Non-Woven & Composites",
  TEXTILES_PROTECTIVE_FABRICS: "Protective & Specialty Fabrics",
  TEXTILES_PROCESSING_FINISHING: "Textile Processing & Finishing",

  // Packaging & Printing
  PACKAGING_FLEXIBLE: "Flexible Packaging",
  PACKAGING_RIGID_INDUSTRIAL: "Rigid & Industrial Packaging",
  PACKAGING_LABELS_PRINTING: "Labels & Printing",
  PACKAGING_PAPER_BOARD: "Paper & Board Packaging",
  PACKAGING_MACHINERY_AUTOMATION: "Packaging Machinery & Automation",

  // ICT & Digital
  DIGITAL_ENTERPRISE_SOFTWARE_SAAS: "Enterprise Software & SaaS",
  DIGITAL_APIS_PLATFORMS: "APIs & Platforms",
  DIGITAL_AI_DATA_ANALYTICS: "AI & Data Analytics",
  DIGITAL_CYBERSECURITY: "Cybersecurity",
  DIGITAL_B2B_PLATFORMS: "B2B Platforms & Marketplaces",

  // Climate Tech & Sustainability
  CLIMATE_CARBON_CAPTURE_MANAGEMENT: "Carbon Capture & Management",
  CLIMATE_CLEAN_MANUFACTURING: "Clean Manufacturing",
  CLIMATE_ENERGY_EFFICIENCY: "Energy Efficiency Solutions",
  CLIMATE_ESG_COMPLIANCE_PLATFORMS: "ESG & Compliance Platforms",
  CLIMATE_CIRCULAR_ECONOMY: "Circular Economy Solutions",
};

// Mapping of Industry to Sub-Industries
export const INDUSTRY_SUB_INDUSTRY_MAP: Record<string, string[]> = {
  OIL_AND_GAS: [
    "OIL_GAS_UPSTREAM_EXPLORATION_PRODUCTION",
    "OIL_GAS_MIDSTREAM_TRANSPORTATION_STORAGE",
    "OIL_GAS_DOWNSTREAM_REFINING_PETROCHEMICALS",
    "OIL_GAS_OILFIELD_EQUIPMENT_SERVICES",
    "OIL_GAS_LNG_GAS_PROCESSING",
    "OIL_GAS_INDUSTRIAL_SAFETY_COMPLIANCE",
  ],
  DEFENCE_AND_AEROSPACE: [
    "DEFENCE_AERO_COMPONENTS_ASSEMBLIES",
    "DEFENCE_AERO_ELECTRONICS_SYSTEMS",
    "DEFENCE_AERO_AVIONICS_NAVIGATION",
    "DEFENCE_AERO_MRO",
    "DEFENCE_AERO_SPACE_SYSTEMS_SATELLITES",
    "DEFENCE_AERO_GROUND_SUPPORT_EQUIPMENT",
  ],
  SHIPPING_MARITIME_PORTS: [
    "MARITIME_SHIPBUILDING_REPAIR",
    "MARITIME_MARINE_EQUIPMENT_SYSTEMS",
    "MARITIME_PORT_INFRASTRUCTURE_HANDLING",
    "MARITIME_LOGISTICS_TECHNOLOGY",
    "MARITIME_OFFSHORE_STRUCTURES_SERVICES",
  ],
  POWER_AND_ENERGY: [
    "ENERGY_THERMAL_POWER_SYSTEMS",
    "ENERGY_RENEWABLE_EQUIPMENT",
    "ENERGY_STORAGE_SYSTEMS",
    "ENERGY_TRANSMISSION_DISTRIBUTION",
    "ENERGY_POWER_PLANT_EPC_OM",
  ],
  MANUFACTURING_AND_INDUSTRIAL_AUTOMATION: [
    "MANUFACTURING_FACTORY_AUTOMATION_ROBOTICS",
    "MANUFACTURING_INDUSTRIAL_CONTROLS_PLCS",
    "MANUFACTURING_SMART_MANUFACTURING_INDUSTRY_4",
    "MANUFACTURING_SENSORS_INSTRUMENTATION",
    "MANUFACTURING_INDUSTRIAL_SOFTWARE",
  ],
  ELECTRONICS_AND_SEMICONDUCTORS: [
    "ELECTRONICS_SEMICONDUCTOR_DEVICES_ICS",
    "ELECTRONICS_PCB_DESIGN_ASSEMBLY",
    "ELECTRONICS_COMPONENTS_CONNECTORS",
    "ELECTRONICS_CONSUMER_INDUSTRIAL",
    "ELECTRONICS_EMS",
  ],
  AGRICULTURE_AND_FOOD_PROCESSING: [
    "AGRI_MACHINERY_EQUIPMENT",
    "AGRI_FOOD_PROCESSING_PACKAGING",
    "AGRI_COLD_CHAIN_STORAGE",
    "AGRI_INPUTS_SEEDS_FERTILISERS",
    "AGRI_PRECISION_FARMING_TECH",
  ],
  AUTOMOTIVE_AND_EV: [
    "AUTOMOTIVE_ICE_COMPONENTS",
    "AUTOMOTIVE_EV_POWERTRAIN_DRIVETRAIN",
    "AUTOMOTIVE_BATTERIES_CHARGING",
    "AUTOMOTIVE_ELECTRONICS",
    "AUTOMOTIVE_TIER1_TIER2_COMPONENTS",
  ],
  CHEMICALS_AND_SPECIALTY_MATERIALS: [
    "CHEMICALS_BASIC_COMMODITY",
    "CHEMICALS_SPECIALTY_PERFORMANCE",
    "CHEMICALS_INDUSTRIAL_GASES",
    "CHEMICALS_COATINGS_ADHESIVES_SEALANTS",
    "CHEMICALS_ADDITIVES_CATALYSTS",
  ],
  MINING: ["METALS_MINING_EQUIPMENT_SERVICES", "METALS_MINERAL_PROCESSING"],
  METALS_AND_MINERALS: ["METALS_FERROUS_NON_FERROUS", "METALS_FOUNDRIES_CASTINGS", "METALS_METAL_FABRICATION"],
  CONSTRUCTION_AND_EPC: [
    "CONSTRUCTION_INFRASTRUCTURE_DEVELOPMENT",
    "CONSTRUCTION_INDUSTRIAL",
    "CONSTRUCTION_EPC_CONTRACTORS",
    "CONSTRUCTION_PRE_ENGINEERED_BUILDINGS",
    "CONSTRUCTION_MATERIALS_SYSTEMS",
  ],
  WATER_AND_ENVIRONMENTAL_SOLUTIONS: [
    "WATER_TREATMENT_SYSTEMS",
    "WATER_WASTEWATER_MANAGEMENT",
    "WATER_DESALINATION_TECHNOLOGIES",
    "WATER_INDUSTRIAL_EFFLUENT_TREATMENT",
    "WATER_ENVIRONMENTAL_MONITORING",
  ],
  HEALTHCARE_AND_LIFE_SCIENCES: [
    "HEALTHCARE_MEDICAL_DEVICES",
    "HEALTHCARE_DIAGNOSTICS_IMAGING",
    "HEALTHCARE_LIFE_SCIENCES_TOOLS",
    "HEALTHCARE_PHARMA_MANUFACTURING",
    "HEALTHCARE_HOSPITAL_INFRASTRUCTURE",
  ],
  ICT_AND_DIGITAL_TECH: [
    "DIGITAL_ENTERPRISE_SOFTWARE_SAAS",
    "DIGITAL_APIS_PLATFORMS",
    "DIGITAL_AI_DATA_ANALYTICS",
    "DIGITAL_CYBERSECURITY",
    "DIGITAL_B2B_PLATFORMS",
  ],
  CLIMATE_TECH_AND_SUSTAINABILITY: [
    "CLIMATE_CARBON_CAPTURE_MANAGEMENT",
    "CLIMATE_CLEAN_MANUFACTURING",
    "CLIMATE_ENERGY_EFFICIENCY",
    "CLIMATE_ESG_COMPLIANCE_PLATFORMS",
    "CLIMATE_CIRCULAR_ECONOMY",
  ],
  INDUSTRIAL_MACHINERY_TOOLS_AND_CAPITAL_EQUIPMENT: [
    "MACHINERY_MACHINE_TOOLS_CNC",
    "MACHINERY_HEAVY_INDUSTRIAL",
    "MACHINERY_PRODUCTION_LINES_TURNKEY",
    "MACHINERY_MATERIAL_HANDLING",
    "MACHINERY_INDUSTRIAL_TOOLING",
  ],
  PLASTICS_POLYMERS_AND_RUBBER_MANUFACTURING: [
    "PLASTICS_INJECTION_MOULDING",
    "PLASTICS_EXTRUSION_BLOW_MOULDING",
    "PLASTICS_RUBBER_ELASTOMERS",
    "PLASTICS_INDUSTRIAL_RESINS",
    "PLASTICS_POLYMER_COMPOUNDING",
  ],
  CONSUMER_PRODUCTS_AND_FMCG: [
    "CONSUMER_HOME_PERSONAL_CARE",
    "CONSUMER_APPLIANCES",
    "CONSUMER_LIFESTYLE_PRODUCTS",
    "CONSUMER_PRIVATE_LABEL",
    "CONSUMER_OEM_ODM",
  ],
  TEXTILES_APPAREL_AND_TECHNICAL_FABRICS: [
    "TEXTILES_APPAREL_GARMENTS",
    "TEXTILES_TECHNICAL_INDUSTRIAL",
    "TEXTILES_NON_WOVEN_COMPOSITES",
    "TEXTILES_PROTECTIVE_FABRICS",
    "TEXTILES_PROCESSING_FINISHING",
  ],
  PACKAGING_PRINTING_AND_CONVERTING_SOLUTIONS: [
    "PACKAGING_FLEXIBLE",
    "PACKAGING_RIGID_INDUSTRIAL",
    "PACKAGING_LABELS_PRINTING",
    "PACKAGING_PAPER_BOARD",
    "PACKAGING_MACHINERY_AUTOMATION",
  ],
};

// Collaboration Intent Labels
export const COLLABORATION_INTENT_LABELS: Record<string, string> = {
  DISTRIBUTION_MARKET_ACCESS: "Distribution and Channel Partnership",
  MANUFACTURING: "Manufacturing / Services",
  TECHNOLOGY_LICENSING: "Technology Licensing",
  RD_CO_DEVELOPMENT: "R&D / Co-Development Partnerships",
  OUTSOURCING_MANAGED_SERVICES_API_SOFTWARE_INTEGRATION: "Outsourcing / Managed Services / API / Software Integration",
  STRATEGIC_JOINT_VENTURE: "Strategic / Joint Venture",
};

// Who Am I Labels (display order matches WHO_AM_I_ORDER)
export const WHO_AM_I_LABELS: Record<string, string> = {
  MANUFACTURER: "Manufacturer",
  DISTRIBUTOR_CHANNEL_PARTNER: "Distributor / Channel Partner",
  TECHNOLOGY_IP_OWNER: "Technology / IP Provider",
  TECHNOLOGY_LICENSEE: "Technology User / Licensee",
  RESEARCH_RND_ORGANIZATION: "R&D Organisation",
  CAPABILITIES_AND_SERVICE_PROVIDER: "Capability & Services Provider",
};

/** Stable ordering for Who Am I dropdowns */
export const WHO_AM_I_ORDER = [
  "MANUFACTURER",
  "DISTRIBUTOR_CHANNEL_PARTNER",
  "TECHNOLOGY_IP_OWNER",
  "TECHNOLOGY_LICENSEE",
  "RESEARCH_RND_ORGANIZATION",
  "CAPABILITIES_AND_SERVICE_PROVIDER",
] as const;

// Long-form descriptions shown on hover in profile forms
export const WHO_AM_I_DESCRIPTIONS: Record<string, string> = {
  MANUFACTURER:
    "You make products or parts, either under your own brand or for another company, with or without selling directly to customers.",
  DISTRIBUTOR_CHANNEL_PARTNER:
    "You handle sales, distribution, and market access for products in specific regions or channels, without owning the product.",
  TECHNOLOGY_IP_OWNER:
    "You own proprietary technology, patents, or know-how and provide it to partners through licensing, integration, or collaboration.",
  TECHNOLOGY_LICENSEE:
    "You adopt or license technology from another company to use in your products, services, or operations.",
  RESEARCH_RND_ORGANIZATION:
    "You develop new products or technologies, either by leading innovation or by supporting others through research, testing, and technical expertise.",
  CAPABILITIES_AND_SERVICE_PROVIDER:
    "You offer specialized services or capabilities (legal, admin, financial design etc) to support business operations or projects.",
};

// Helper function to get who am I label
export const getWhoAmILabel = (enumValue: string): string => {
  return WHO_AM_I_LABELS[enumValue] || enumValue;
};

export const getWhoAmIDescription = (enumValue: string): string | undefined => {
  return WHO_AM_I_DESCRIPTIONS[enumValue];
};

// Helper function to get who am I options for Select
export const getWhoAmIOptions = (): { value: string; label: string }[] => {
  return WHO_AM_I_ORDER.map((value) => ({
    value,
    label: WHO_AM_I_LABELS[value],
  }));
};

// Helper function to get sub-industry label
export const getSubIndustryLabel = (enumValue: string): string => {
  return SUB_INDUSTRY_LABELS[enumValue] || enumValue;
};

// Helper function to get sub-industry options for Select
export const getSubIndustryOptions = (): { value: string; label: string }[] => {
  return Object.entries(SUB_INDUSTRY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
};

// Helper function to get sub-industries by industry
export const getSubIndustriesByIndustry = (industryValue: string): { value: string; label: string }[] => {
  const subIndustryKeys = INDUSTRY_SUB_INDUSTRY_MAP[industryValue] || [];
  return subIndustryKeys.map((key) => ({
    value: key,
    label: SUB_INDUSTRY_LABELS[key] || key,
  }));
};

// Helper function to get collaboration intent label
export const getCollaborationIntentLabel = (enumValue: string): string => {
  return COLLABORATION_INTENT_LABELS[enumValue] || enumValue;
};

// Helper function to get collaboration intent options for Select
export const getCollaborationIntentOptions = (): { value: string; label: string }[] => {
  return Object.entries(COLLABORATION_INTENT_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
};

// Helper function to get display label from enum value
export const getIndustryLabel = (enumValue: string): string => {
  return OPERATING_INDUSTRY_LABELS[enumValue] || enumValue;
};

// Helper function to get options for Select components
export const getIndustryOptions = (): { value: string; label: string }[] => {
  return Object.entries(OPERATING_INDUSTRY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
};

// Helper function to get all industry enum values
export const getIndustryValues = (): string[] => {
  return Object.keys(OPERATING_INDUSTRY_LABELS);
};

// Regions Data with Countries (canonical catalog in shared/collaborationRegionsCatalog.ts)
export type Region = CollaborationRegion;
export type RegionsData = CollaborationRegionsData;
export const regionsData: RegionsData = collaborationRegionsData;

export const REGIONS_FILTER_DATA = regionsData.regions.map((region) => ({
  value: region.id,
  label: region.name,
}));

// Helper function to get all region names for Select dropdown
export const getRegionNamesForSelect = (): string[] => {
  return regionsData.regions.map((region) => region.name);
};

// Helper function to get region options for Select with value/label
export const getRegionOptionsForSelect = (): { value: string; label: string }[] => {
  return regionsData.regions.map((region) => ({
    value: region.id,
    label: region.name,
  }));
};

// Helper function to get countries by region id
export const getCountriesByRegion = (regionId: string): string[] => {
  const region = regionsData.regions.find((r) => r.id === regionId);
  return region ? region.countries : [];
};

// Helper function to get region by country name
export const getRegionByCountry = (countryName: string): Region | null => {
  for (const region of regionsData.regions) {
    if (region.countries.includes(countryName)) {
      return region;
    }
  }
  return null;
};

// Helper function to get all countries from all regions (flat list)
export const getAllCountriesFromRegions = (): string[] => {
  return regionsData.regions.flatMap((region) => region.countries);
};

export const STATUS_COLORS = {
  SUCCESS: {
    bg: "#F0FDF4",
    icon: "#008236",
    text: "#008236",
    border: "#008236",
  },
  WARNING: {
    bg: "#FEF3C7",
    icon: "#E17100",
    text: "#E17100",
    border: "#E17100",
  },
  ERROR: {
    bg: "#FEE2E2",
    icon: "#DC2626",
    text: "#DC2626",
    border: "#FECACA",
  },
};

export const FileCategory = {
  COMPANY_LOGO: "COMPANY_LOGO",
  COMPANY_IMAGE: "COMPANY_IMAGE",
  COMPANY_DOCUMENT: "COMPANY_DOCUMENT",
  ADDITIONAL_IMAGES: "ADDITIONAL_IMAGES",
} as const;

export const ProductServiceType = {
  PRODUCT: "PRODUCT",
  SERVICE: "SERVICE",
} as const;

export type ProductServiceType = (typeof ProductServiceType)[keyof typeof ProductServiceType];

export const CollaborationStatus = {
  REQUESTED: "REQUESTED",
  RECEIVER_DECLINED: "RECEIVER_DECLINED",
  BOTH_ACCEPTED: "BOTH_ACCEPTED",
  REQUESTER_PAID_ONLY: "REQUESTER_PAID_ONLY",
  RECEIVER_PAID_ONLY: "RECEIVER_PAID_ONLY",
  PAYMENT_EXPIRED: "PAYMENT_EXPIRED",
  BOTH_PAID: "BOTH_PAID",
} as const;

export const CollaborationPrice = "1 Collaboration";

// ==================== COLLABORATION ROLE LABELS ====================

export const COLLABORATION_ROLE_LABELS: Record<string, string> = {
  BRAND_OWNER_OEM_PRODUCT_OWNER: "Brand Owner / OEM / Product Owner",
  CONTRACT_MANUFACTURER_SUPPLIER: "Contract Manufacturer / Supplier",
  DISTRIBUTOR_CHANNEL_PARTNER: "Distributor / Channel Partner",
  TECHNOLOGY_IP_PROVIDER: "Technology / IP Provider",
  TECHNOLOGY_USER_LICENSEE: "Technology User / Licensee",
  RND_LEAD_INNOVATION_OWNER: "R&D Lead / Innovation Owner",
  RND_PARTNER_CONTRIBUTOR: "R&D Partner / Contributor",
  CAPABILITY_SERVICES_PROVIDER: "Capability & Services Provider",
  SOLUTION_SEEKER: "Solution Seeker",
  STRATEGIC_JV_PARTNER: "Strategic / JV Partner",
  LEAD_DISTRIBUTOR: "Lead Distributor",
  REGIONAL_DISTRIBUTOR: "Regional Distributor",
};

export const getCollaborationRoleLabel = (enumValue: string): string => {
  return COLLABORATION_ROLE_LABELS[enumValue] || enumValue;
};

export const getCollaborationRoleOptions = (): { value: string; label: string }[] => {
  return Object.entries(COLLABORATION_ROLE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
};

/** Fixed role list for manual explore-opportunities (both dropdowns). */
export const MANUAL_EXPRESS_INTEREST_ROLES = [
  "LEAD_DISTRIBUTOR",
  "BRAND_OWNER_OEM_PRODUCT_OWNER",
  "TECHNOLOGY_IP_PROVIDER",
  "RND_LEAD_INNOVATION_OWNER",
  "CAPABILITY_SERVICES_PROVIDER",
  "STRATEGIC_JV_PARTNER",
] as const;

export const MANUAL_EXPRESS_INTEREST_ROLE_LABELS: Record<string, string> = {
  LEAD_DISTRIBUTOR: "Lead Distributor",
  BRAND_OWNER_OEM_PRODUCT_OWNER: "Brand Owner / OEM",
  TECHNOLOGY_IP_PROVIDER: "Technology / IP Provider",
  RND_LEAD_INNOVATION_OWNER: "R&D Lead / Innovation Owner",
  CAPABILITY_SERVICES_PROVIDER: "Capability & Services Provider",
  STRATEGIC_JV_PARTNER: "Strategic / JV Partner",
};

export function getManualExpressInterestRoleOptions(): { value: string; label: string }[] {
  return MANUAL_EXPRESS_INTEREST_ROLES.map((value) => ({
    value,
    label: MANUAL_EXPRESS_INTEREST_ROLE_LABELS[value] ?? getCollaborationRoleLabel(value),
  }));
}

export function getManualExpressInterestRoleLabel(role: string): string {
  return MANUAL_EXPRESS_INTEREST_ROLE_LABELS[role] ?? getCollaborationRoleLabel(role);
}

// ==================== WHO AM I → MY ROLE MAPPING ====================

export const WHO_AM_I_TO_ROLES: Record<string, string[]> = {
  MANUFACTURER: ["BRAND_OWNER_OEM_PRODUCT_OWNER", "CONTRACT_MANUFACTURER_SUPPLIER"],
  DISTRIBUTOR_CHANNEL_PARTNER: ["DISTRIBUTOR_CHANNEL_PARTNER"],
  TECHNOLOGY_IP_OWNER: ["TECHNOLOGY_IP_PROVIDER", "TECHNOLOGY_USER_LICENSEE"],
  TECHNOLOGY_LICENSEE: ["TECHNOLOGY_IP_PROVIDER", "TECHNOLOGY_USER_LICENSEE"],
  CAPABILITIES_AND_SERVICE_PROVIDER: ["CAPABILITY_SERVICES_PROVIDER", "SOLUTION_SEEKER"],
  RESEARCH_RND_ORGANIZATION: ["RND_LEAD_INNOVATION_OWNER", "RND_PARTNER_CONTRIBUTOR"],
};

// Given an array of whoAmI values, returns the deduplicated role options for Q2
// Always includes STRATEGIC_JV_PARTNER
export const getMyRoleOptions = (whoAmIValues: string[]): { value: string; label: string }[] => {
  const roleSet = new Set<string>();
  whoAmIValues.forEach((w) => {
    const roles = WHO_AM_I_TO_ROLES[w] || [];
    roles.forEach((r) => roleSet.add(r));
  });
  roleSet.add("STRATEGIC_JV_PARTNER"); // always visible
  return Array.from(roleSet).map((value) => ({
    value,
    label: COLLABORATION_ROLE_LABELS[value] || value,
  }));
};

// ==================== COLLABORATION INTENT → EXPECTED PARTNER ROLE MAPPING ====================

export const INTENT_TO_PARTNER_ROLES: Record<string, string[]> = {
  DISTRIBUTION_MARKET_ACCESS: ["LEAD_DISTRIBUTOR", "REGIONAL_DISTRIBUTOR"],
  MANUFACTURING: ["BRAND_OWNER_OEM_PRODUCT_OWNER", "CONTRACT_MANUFACTURER_SUPPLIER"],
  TECHNOLOGY_LICENSING: ["TECHNOLOGY_IP_PROVIDER", "TECHNOLOGY_USER_LICENSEE"],
  RD_CO_DEVELOPMENT: ["RND_LEAD_INNOVATION_OWNER", "RND_PARTNER_CONTRIBUTOR"],
  OUTSOURCING_MANAGED_SERVICES_API_SOFTWARE_INTEGRATION: ["CAPABILITY_SERVICES_PROVIDER", "SOLUTION_SEEKER"],
  STRATEGIC_JOINT_VENTURE: ["STRATEGIC_JV_PARTNER"],
};

// Given an array of collaborationIntent values (from target company), returns the expected partner role options for Q3
// Always includes STRATEGIC_JV_PARTNER
export const getExpectedPartnerRoleOptions = (collaborationIntents: string[]): { value: string; label: string }[] => {
  const roleSet = new Set<string>();
  collaborationIntents.forEach((intent) => {
    const roles = INTENT_TO_PARTNER_ROLES[intent] || [];
    roles.forEach((r) => roleSet.add(r));
  });
  roleSet.add("STRATEGIC_JV_PARTNER"); // always visible
  return Array.from(roleSet).map((value) => ({
    value,
    label: COLLABORATION_ROLE_LABELS[value] || value,
  }));
};

// ==================== ADDITIONAL TECHNOLOGY & INFRASTRUCTURE ====================

export const ADDITIONAL_TECH_INFRASTRUCTURE_LABELS: Record<string, string> = {
  INHOUSE_RND_FACILITY: "In-house Research & Development (R&D) Facility",
  PRODUCT_TESTING_QUALITY_CERT_LAB: "Product Testing & Quality Certification Laboratory",
  PROTOTYPE_PILOT_PRODUCTION_FACILITY: "Prototype Development & Pilot Production Facility",
  TOOLING_MOULDING_DIE_MAKING: "Tooling, Moulding & Die-Making Facility",
  ADVANCED_MANUFACTURING_CNC: "Advanced Manufacturing / CNC Machining Facility",
  AUTOMATION_ROBOTICS_SMART_FACTORY: "Automation, Robotics & Smart Factory Setup",
  ENVIRONMENTAL_STRESS_TESTING: "Environmental & Stress Testing Facility",
  CALIBRATION_METROLOGY_LAB: "Calibration & Metrology Laboratory",
  COLD_STORAGE_TEMPERATURE_CONTROLLED: "Cold Storage & Temperature-Controlled Infrastructure",
  CLEAN_ROOM_ISO_GMP: "Clean Room Facility (ISO / GMP compliant)",
  MATERIALS_TESTING_METALLURGICAL_LAB: "Materials Testing & Metallurgical Lab",
  PACKAGING_LABELLING_COMPLIANCE: "Packaging, Labelling & Compliance Facility",
  WAREHOUSE_LOGISTICS_INVENTORY: "Warehouse, Logistics & Inventory Management Infrastructure",
  DIGITAL_INFRASTRUCTURE_CLOUD_IT: "Digital Infrastructure (Cloud, API, Data & IT Systems)",
  TRAINING_SKILL_DEVELOPMENT_FACILITY: "Training, Skill Development & Technical Support Facility",
};

export const getAdditionalTechInfraLabel = (enumValue: string): string => {
  return ADDITIONAL_TECH_INFRASTRUCTURE_LABELS[enumValue] || enumValue;
};

export const getAdditionalTechInfraOptions = (): { value: string; label: string }[] => {
  return Object.entries(ADDITIONAL_TECH_INFRASTRUCTURE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
};

export const CollaborationRole = {
  BRAND_OWNER_OEM_PRODUCT_OWNER: "BRAND_OWNER_OEM_PRODUCT_OWNER",
  CONTRACT_MANUFACTURER_SUPPLIER: "CONTRACT_MANUFACTURER_SUPPLIER",
  DISTRIBUTOR_CHANNEL_PARTNER: "DISTRIBUTOR_CHANNEL_PARTNER",
  TECHNOLOGY_IP_PROVIDER: "TECHNOLOGY_IP_PROVIDER",
  TECHNOLOGY_USER_LICENSEE: "TECHNOLOGY_USER_LICENSEE",
  RND_LEAD_INNOVATION_OWNER: "RND_LEAD_INNOVATION_OWNER",
  RND_PARTNER_CONTRIBUTOR: "RND_PARTNER_CONTRIBUTOR",
  CAPABILITY_SERVICES_PROVIDER: "CAPABILITY_SERVICES_PROVIDER",
  SOLUTION_SEEKER: "SOLUTION_SEEKER",
  STRATEGIC_JV_PARTNER: "STRATEGIC_JV_PARTNER",
  LEAD_DISTRIBUTOR: "LEAD_DISTRIBUTOR",
  REGIONAL_DISTRIBUTOR: "REGIONAL_DISTRIBUTOR",
} as const;
